import React, {
    CSSProperties,
    ForwardedRef,
    PropsWithoutRef,
    RefAttributes,
    useCallback,
    useDeferredValue,
    useMemo,
    useRef,
    useState,
} from "react";
import mergeRefs from "merge-refs";

import { useStableReferenceValue } from "../../hooks/useStableReferenceValue";
import { useUpdateEffect } from "../../utils/useUpdateEffect";
import { Typography } from "../common/Typography";
import { FilteredItems } from "./FilteredItems";
import { filterItemsByTabs, searchItemsFor } from "./filters";
import { BasicHeader, Container, Header } from "./Picker.styles";
import {
    DisplayType,
    IPickerGroup,
    IPickerTab,
    isPickerButton,
    isPickerLink,
    PickerItem,
} from "./Picker.types";
import { PickerDisplayContextProvider } from "./PickerDisplayContext";
import { PickerTabs } from "./PickerTabs";
import { SearchField } from "./SearchField";
import { SelectionCanceller } from "./SelectionCanceller";
import { useIndexedItem } from "./useIndexedItem";
import { useScopedKeys } from "./useScopedKeys";

export interface IPickerProps<TTab extends string, TGroup extends string> {
    className?: string;
    tabs?: IPickerTab<TTab>[];
    groups?: IPickerGroup<TGroup>[];
    items: PickerItem<TTab, TGroup>[];
    noAutoSelectFirst?: boolean;
    noSearch?: boolean;

    selectedTab?: TTab | null;
    onSelectTab?: (id: TTab) => void;

    onSelectItem?: (item: PickerItem<TTab, TGroup>) => void;
    onExit?: () => void;

    searchPlaceholder?: string;
    listMode?: "list";
    displayType?: DisplayType;
    style?: CSSProperties;
}

const emptyGroups: IPickerGroup<never>[] = [];
const emptyTabs: IPickerTab<never>[] = [];

function Picker<TTab extends string = string, TGroup extends string = string>(
    props: IPickerProps<TTab, TGroup>,
    ref: ForwardedRef<HTMLElement>,
) {
    const {
        className,
        items,
        onExit,
        onSelectItem,
        onSelectTab,
        searchPlaceholder,
        style,

        displayType = "dropdown",
        groups: definedGroups = emptyGroups,
        noAutoSelectFirst = false,
        tabs: definedTabs = emptyTabs,

        selectedTab: initialSelectedTab,
        noSearch,
    } = props;
    const [query, setQuery] = useState<string>("");
    const searchQuery = useDeferredValue(query);
    const isSearching = searchQuery !== "";

    const allTabIds = useStableReferenceValue([
        ...new Set(items.flatMap(it => (it.tab ? it.tab : []))).values(),
    ]);

    const allGroupIds = useStableReferenceValue([
        ...new Set(items.flatMap(it => (it.group ? it.group : []))).values(),
    ]);

    const tabs = useMemo(() => {
        const simpleTabs = allTabIds
            .filter(it => definedTabs.every(tab => !(tab.id === it)))
            .map<IPickerTab<TTab>>(it => ({
                id: it,
                label: it,
            }));
        return [...simpleTabs, ...definedTabs];
    }, [definedTabs, allTabIds]);

    const groups = useMemo(() => {
        const simpleGroups = allGroupIds
            .filter(it => definedGroups.every(group => !(group.id === it)))
            .map<IPickerGroup<TGroup>>(it => ({
                id: it,
                label: it,
            }));
        return [...simpleGroups, ...definedGroups];
    }, [definedGroups, allGroupIds]);

    const selectedTab = initialSelectedTab ?? tabs[0]?.id ?? null;

    const [filteredItems, total] = useMemo(() => {
        return isSearching
            ? searchItemsFor(items, tabs, searchQuery)
            : filterItemsByTabs(selectedTab, items);
    }, [isSearching, items, tabs, searchQuery, selectedTab]);

    const [index, setIndex] = useState(noAutoSelectFirst ? -1 : 0);
    const clearIndex = useCallback(() => setIndex(-1), [setIndex]);
    const deferredIndex = useDeferredValue(index);

    const [indexedItem, setIndexedItem] = useIndexedItem(
        deferredIndex,
        filteredItems,
        setIndex,
    );

    const handleSelectItem = useCallback(
        (item: PickerItem<TTab, TGroup>) => {
            if (onSelectItem) {
                onSelectItem(item);
                clearIndex();
                setQuery("");
            }
        },
        [onSelectItem, clearIndex],
    );

    const tabsRef = useRef<HTMLDivElement>(null);

    const keyboardRef = useScopedKeys<HTMLElement>({
        Escape: useCallback(() => {
            if (searchQuery) {
                setQuery("");
            } else {
                onExit?.();
            }
        }, [onExit, searchQuery, setQuery]),
        Enter: useCallback(() => {
            if (tabsRef.current?.contains(document.activeElement)) {
                return;
            }

            if (indexedItem && !indexedItem.disabled) {
                const canceller = new SelectionCanceller();
                if (isPickerLink(indexedItem)) {
                    indexedItem.onClick?.(canceller);
                } else if (isPickerButton(indexedItem)) {
                    indexedItem.onClick?.(canceller);
                } else {
                    throw new Error("unrecognized item");
                }

                if (!canceller.isCancelled) {
                    handleSelectItem(indexedItem);
                }
            }
        }, [indexedItem, handleSelectItem, tabsRef]),
        ArrowDown: useCallback(
            (e: KeyboardEvent) => {
                if (total > 0) {
                    e.preventDefault();
                    e.stopPropagation();
                    setIndex(it => Math.max(0, Math.min(it + 1, total - 1)));
                }
            },
            [total],
        ),
        ArrowUp: useCallback(
            (e: KeyboardEvent) => {
                if (total) {
                    e.preventDefault();
                    e.stopPropagation();
                    setIndex(it => Math.max(0, Math.min(it - 1, total - 1)));
                }
            },
            [total],
        ),
    });

    useUpdateEffect(() => {
        setIndex(0);
    }, [searchQuery, selectedTab, setIndex]);

    return (
        <PickerDisplayContextProvider displayType={displayType}>
            <Container
                ref={mergeRefs(ref, keyboardRef)}
                className={className}
                style={style}
            >
                <Header>
                    {noSearch ? (
                        <BasicHeader>
                            <Typography type="minimum" uppercase={true}>
                                {searchPlaceholder}
                            </Typography>
                        </BasicHeader>
                    ) : (
                        <SearchField
                            value={query}
                            onChange={setQuery}
                            placeholder={searchPlaceholder}
                        />
                    )}
                    <div ref={tabsRef}>
                        {tabs.length && tabs.length > 1 ? (
                            <PickerTabs
                                selectedTab={selectedTab ?? tabs[0].id}
                                onSelectTab={onSelectTab}
                                tabs={tabs}
                                items={items}
                                isHidden={isSearching}
                            />
                        ) : null}
                    </div>
                </Header>
                <FilteredItems
                    groups={groups}
                    indexedItem={indexedItem}
                    setIndexedItem={setIndexedItem}
                    clearIndexedItem={clearIndex}
                    onSelectItem={handleSelectItem}
                >
                    {filteredItems}
                </FilteredItems>
            </Container>
        </PickerDisplayContextProvider>
    );
}

const ForwardedPicker = React.forwardRef(Picker) as <
    TTab extends string = string,
    TGroup extends string = string,
>(
    props: PropsWithoutRef<IPickerProps<TTab, TGroup>> &
        RefAttributes<HTMLElement>,
) => JSX.Element;

export { ForwardedPicker as Picker };
